import '../JobQueue/JobQueue.scss';

import FilterListIcon from '@mui/icons-material/FilterList';
import { TablePagination, Typography } from '@mui/material';
import { CandidateSubmissionType } from 'components/JobQueue/utils';
import React, { useState } from 'react';
import { getAllListData } from 'services/CommonServices';
import { SALES_ENDPOINT } from 'utils/Constant';

import CandidateTableHeader from './MoreCandidateHeader';
import CandidateTable from './MoreCandidateTables';

const MoreCandidate: React.FC<{
  jobId: string;
  goBack: () => void;
  score?: string;
  goal?: string;
  jobTitle?: string;
  expFrom?: number | null;
  expTo?: number | null;
  // matchesList: CandidateSubmissionType[];
}> = ({ goBack, jobId, score, goal, jobTitle, expFrom, expTo }) => {
  const [matchesList, setMatchesList] = useState<CandidateSubmissionType[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [totalCount, setTotalCount] = React.useState<number>(0);
  const [params, setParams] = React.useState<any>({
    score: score || '',
    search: '',
    skip: 0,
    take: 50,
  });

  const [, setOpenFilter] = useState(false);

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setParams((prev) => ({
      ...prev,
      search: value,
      skip: 0,
      take: prev.take || 50,
    }));
  };

  const handleRowSelect = (id: string) => {
    setSelectedRows((prev) => {
      if (prev.includes(id)) {
        return prev.filter((rowId) => rowId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      // Select all visible rows (you might want to implement select all across all pages)
      setSelectedRows([]);
    } else {
      setSelectedRows([]);
    }
  };

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setParams((prev) => ({
      ...prev,
      skip: newPage * Number(prev.take),
    }));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setParams((prev) => ({
      ...prev,
      skip: 0,
      take: parseInt(event.target.value, 10),
    }));
  };

  const getMatchesList = React.useCallback(
    async (
      id: string,
      skip: number,
      take: number,
      search?: string,
      filterScore?: string,
    ) => {
      setLoading(true);
      const url = `${
        SALES_ENDPOINT.MatchedCandidates
      }/${id}?type=matched&skip=${String(skip)}&take=${String(take)}${
        search ? `&search=${String(search)}` : ''
      }${
        filterScore ? `&score=${String(filterScore)} ` : ''
      }&sourceQueue=${'benchQueue'}`;

      await getAllListData(url)
        .then((res) => {
          setMatchesList(res?.data || []);
          setTotalCount(res?.total || res?.data?.length || 0);
        })
        .catch((err: any) => {
          setMatchesList([]);
          setTotalCount(0);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [],
  );

  React.useEffect(() => {
    if (!jobId) return;

    // Debounce search to avoid too many API calls
    const timeoutId = setTimeout(
      () => {
        getMatchesList(
          jobId,
          params.skip,
          params.take,
          params.search || '',
          params.score || '',
        );
      },
      params.search ? 500 : 0,
    );

    return () => clearTimeout(timeoutId);
  }, [
    jobId,
    params.skip,
    params.take,
    params.search,
    params.score,
    getMatchesList,
  ]);

  return (
    <Typography component={'div'} className="job-queue-container">
      {/* Header Component */}
      <CandidateTableHeader
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        goBack={() => goBack()}
      />

      {/* Table Component */}
      <div className="job-queue-table-container">
        <div className="job-queue-table-container-content">
          <div className="job-queue-table-container-tabs">
            <CandidateTable
              users={matchesList || []}
              selectedRows={selectedRows}
              onRowSelect={handleRowSelect}
              onSelectAll={handleSelectAll}
              loading={loading}
              jobId={jobId}
              goal={goal}
              jobTitle={jobTitle}
              expFrom={expFrom}
              expTo={expTo}
            />
          </div>

          <div
            className="job-queue-table-container-filters"
            onClick={() => setOpenFilter(true)}>
            <FilterListIcon />
            <Typography
              component="div"
              sx={{
                color: '#181D1F',
                fontFamily: 'Red Hat Display Regular',
                fontSize: '13px',
                fontWeight: 400,
                rotate: '90deg',
              }}>
              Filters
            </Typography>
          </div>
        </div>

        {/* Pagination Component */}
        <div className="job-queue-pagination-container">
          <TablePagination
            sx={{
              '& .MuiTablePagination-select': {
                fontFamily: 'Inter !important',
              },
              '& p': {
                fontFamily: 'Inter !important',
              },
            }}
            component="div"
            count={totalCount || 0}
            page={Number(params.skip) / Number(params.take)}
            onPageChange={handlePageChange}
            rowsPerPage={params.take ? parseInt(String(params.take), 10) : 50}
            rowsPerPageOptions={[50, 100, 150, 200, 250]}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </div>
      </div>
    </Typography>
  );
};

export default MoreCandidate;
